import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  Chip, Alert, CircularProgress, Grid, Card, CardContent, ListSubheader, Tooltip
} from '@mui/material';
import {
  FileDownload as DownloadIcon, CheckCircle as FiledIcon,
  Refresh as GenerateIcon, InfoOutlined
} from '@mui/icons-material';
import {
  getTaxFilings, generate941, generate940, generateW2, generateAllW2, getSupportedTaxYears,
  markTaxFiled, downloadTaxFiling, getEmployees, generatePaLocalForm, downloadTaxFilingCsv
} from '../api';
import { useFeatures } from '../context/FeaturesContext';
import { isPayrollReadOnly, PAYROLL_READ_ONLY_TEXT } from '../utils/featureFlags';
import W2DeliveryPanel from './W2DeliveryPanel';

const FILING_LABELS = {
  '941_quarterly': 'Form 941 (Quarterly)',
  '940_annual': 'Form 940 (Annual)',
  'w2': 'Form W-2',
  'pa_uc2_quarterly': 'PA UC-2 / UC-2A (Quarterly)',
  'pa_w3_quarterly': 'PA-W3 (Quarterly)',
  'pa_rev1667_annual': 'PA REV-1667 W-2 Transmittal (Annual)',
  'local_eit_quarterly': 'Local EIT Return (Quarterly)',
  'local_lst_quarterly': 'Local Services Tax (Quarterly)',
};

// Generate dialog value -> API form (plan 116)
const PA_LOCAL_GEN = { pa_uc2: 'uc2', pa_w3: 'w3', pa_rev1667: 'rev1667', local_eit: 'local_eit', local_lst: 'local_lst' };
const QUARTERLY_GEN = new Set(['941', 'pa_uc2', 'pa_w3', 'local_eit', 'local_lst']);
const CSV_TYPES = new Set(['pa_uc2_quarterly', 'pa_w3_quarterly', 'pa_rev1667_annual', 'local_eit_quarterly', 'local_lst_quarterly']);
const FORM_HELP = {
  '941': 'Quarterly federal return worksheet: wages, federal income tax withheld, Social Security and Medicare.',
  '940': 'Annual FUTA return worksheet, with Part 5 liability by quarter.',
  'w2': 'Wage and Tax Statement for one employee for the tax year.',
  'all-w2': 'Generates a W-2 for every employee paid in the tax year.',
  pa_uc2: 'Pennsylvania unemployment report for the quarter: UC-2 items 1-9 and the UC-2A wage list with credit weeks. File it in the PA UC system.',
  pa_w3: 'Pennsylvania quarterly withholding reconciliation: PA compensation, PA tax withheld and the period breakdown. File it in myPATH.',
  pa_rev1667: 'Pennsylvania annual W-2 transmittal: W-2 count and PA totals from the generated W-2s, with the four quarters. Generate current W-2s first.',
  local_eit: 'Act 32 quarterly local earned income tax return, one worksheet per local tax collector, with each employee\'s PSD codes.',
  local_lst: 'Quarterly Local Services Tax worksheet, one per local tax collector.',
};

const STATUS_COLORS = {
  draft: 'default',
  generated: 'info',
  filed: 'success',
};

const currentYear = new Date().getFullYear();

export default function TaxFilings() {
  // Payroll off + payroll history: forms for wages already paid can still be
  // generated, downloaded and marked filed (the server allows these), so the
  // screen only explains the mode.
  const { features } = useFeatures();
  const readOnly = isPayrollReadOnly(features);
  const [filings, setFilings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [yearFilter, setYearFilter] = useState(currentYear);
  // Only years we ship IRS constants for. Offering a year without reference
  // data produced an error instead of a form — the hard-fail is deliberate, the
  // dropdown advertising it was not.
  const [supportedYears, setSupportedYears] = useState([currentYear]);

  // Generate dialog
  const [genDialog, setGenDialog] = useState(false);
  const [genType, setGenType] = useState('941');
  const [genQuarter, setGenQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [genEmployeeId, setGenEmployeeId] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [filingsRes, empRes] = await Promise.all([
        getTaxFilings({ tax_year: yearFilter }),
        getEmployees()
      ]);
      setFilings(filingsRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      setError('Failed to load tax filings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [yearFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getSupportedTaxYears()
      .then(res => {
        const years = res.data?.years || [];
        if (years.length > 0) {
          setSupportedYears(years);
          if (!years.includes(yearFilter)) setYearFilter(years[0]);
        }
      })
      .catch(() => { /* keep the current year as the only option */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');

      if (genType === '941') {
        await generate941({ tax_year: yearFilter, quarter: genQuarter });
        setSuccess(`Form 941 Q${genQuarter} ${yearFilter} generated`);
      } else if (genType === '940') {
        await generate940({ tax_year: yearFilter });
        setSuccess(`Form 940 ${yearFilter} generated`);
      } else if (genType === 'w2') {
        if (genEmployeeId) {
          await generateW2({ tax_year: yearFilter, employee_id: genEmployeeId });
          setSuccess(`W-2 generated for selected employee`);
        }
      } else if (genType === 'all-w2') {
        const res = await generateAllW2({ tax_year: yearFilter });
        setSuccess(`Generated ${res.data.generated} of ${res.data.total} W-2 forms`);
      } else if (PA_LOCAL_GEN[genType]) {
        const res = await generatePaLocalForm({ form: PA_LOCAL_GEN[genType], tax_year: yearFilter, quarter: genQuarter });
        const n = res.data.filings.length;
        const skipped = res.data.skipped_filed || [];
        const review = (res.data.review_no_eit || []).map((r) => r.name);
        const reviewMsg = review.length ? `. Review - PSD codes on file but no local EIT withheld: ${review.join('; ')}` : '';
        setSuccess((n === 0 && skipped.length === 0
          ? `Nothing to generate: no local tax was withheld in Q${genQuarter} ${yearFilter}`
          : `Generated ${n} worksheet${n === 1 ? '' : 's'}${skipped.length ? `; not regenerated because already filed: ${skipped.join(', ')}` : ''}`) + reviewMsg);
      }

      setGenDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate form');
      if (err.response?.status === 409) {
        // Filed return: close the dialog so the Alert is visible, and refresh the table.
        setGenDialog(false);
        fetchData();
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleMarkFiled = async (id) => {
    try {
      await markTaxFiled(id);
      setSuccess('Filing marked as submitted');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark filing');
    }
  };

  const handleDownload = async (id) => {
    try {
      await downloadTaxFiling(id);
    } catch (err) {
      setError('Failed to download filing');
    }
  };

  const handleDownloadCsv = async (id) => {
    try {
      await downloadTaxFilingCsv(id);
    } catch (err) {
      setError('Failed to download worksheet CSV');
    }
  };

  // Group filings by type
  const form941s = filings.filter(f => f.filing_type === '941_quarterly');
  const form940s = filings.filter(f => f.filing_type === '940_annual');
  const w2s = filings.filter(f => f.filing_type === 'w2');

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">Tax Filing</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Tax Year</InputLabel>
            <Select value={yearFilter} label="Tax Year" onChange={e => setYearFilter(e.target.value)}>
              {supportedYears.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<GenerateIcon />} onClick={() => setGenDialog(true)}>
            Generate Form
          </Button>
        </Box>
      </Box>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2, py: 0 }}>{PAYROLL_READ_ONLY_TEXT}</Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">941 Quarterly</Typography>
              <Typography variant="h4">{form941s.length}<Typography component="span" variant="caption">/4</Typography></Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">940 Annual</Typography>
              <Typography variant="h4">{form940s.length > 0 ? '1' : '0'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">W-2 Forms</Typography>
              <Typography variant="h4">{w2s.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Filed</Typography>
              <Typography variant="h4" color="success.main">
                {filings.filter(f => f.status === 'filed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filings Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Form Type</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Generated</TableCell>
              <TableCell>Filed</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filings.map(filing => (
              <TableRow key={filing.id}>
                <TableCell>
                  <Typography fontWeight="medium">
                    {FILING_LABELS[filing.filing_type] || filing.filing_type}
                  </Typography>
                  {filing.collector_key && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {filing.filing_data?.collector?.name || filing.collector_key}
                    </Typography>
                  )}
                  {filing.filing_data?.warnings?.length > 0 && (
                    <Tooltip title={filing.filing_data.warnings.join(' | ')}>
                      <Chip label="Review" color="warning" size="small" sx={{ mt: 0.5 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {filing.quarter
                    ? `Q${filing.quarter} ${filing.tax_year}`
                    : String(filing.tax_year)}
                </TableCell>
                <TableCell>
                  {filing.employee_first_name
                    ? `${filing.employee_first_name} ${filing.employee_last_name}`
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip label={filing.status} color={STATUS_COLORS[filing.status]} size="small" />
                </TableCell>
                <TableCell>
                  {filing.generated_at ? new Date(filing.generated_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  {filing.filed_at ? new Date(filing.filed_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {filing.pdf_filename && (
                      <Button size="small" startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(filing.id)}>
                        PDF
                      </Button>
                    )}
                    {CSV_TYPES.has(filing.filing_type) && filing.pdf_filename && (
                      <Button size="small" startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadCsv(filing.id)}>
                        CSV
                      </Button>
                    )}
                    {filing.status === 'generated' && (
                      <Button size="small" color="success" startIcon={<FiledIcon />}
                        onClick={() => handleMarkFiled(filing.id)}>
                        Mark Filed
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filings.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No tax filings for {yearFilter}. Generate forms using the button above.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Generated PDFs are worksheets for review. File Forms 941 and 940 on paper or through IRS e-file (MeF); file W-2 Copy A with SSA through Business Services Online. Pennsylvania and local worksheets are filed by hand: UC-2 in the PA UC system, PA-W3 and REV-1667 in myPATH, local EIT and LST with each collector.
      </Typography>

      <Box sx={{ mt: 3 }}>
        <W2DeliveryPanel taxYear={yearFilter} refreshKey={filings} />
      </Box>

      {/* Generate Dialog */}
      <Dialog open={genDialog} onClose={() => setGenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Tax Form</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Form Type</InputLabel>
                <Select value={genType} label="Form Type" onChange={e => setGenType(e.target.value)}>
                  <MenuItem value="941">Form 941 — Quarterly Federal Tax Return</MenuItem>
                  <MenuItem value="940">Form 940 — Annual FUTA Tax Return</MenuItem>
                  <MenuItem value="w2">Form W-2 — Single Employee</MenuItem>
                  <MenuItem value="all-w2">Form W-2 — All Employees</MenuItem>
                  <ListSubheader>Pennsylvania and local (Act 32)</ListSubheader>
                  <MenuItem value="pa_uc2">PA UC-2 / UC-2A — Quarterly Unemployment Report</MenuItem>
                  <MenuItem value="pa_w3">PA-W3 — Quarterly Withholding Reconciliation</MenuItem>
                  <MenuItem value="pa_rev1667">PA REV-1667 — Annual W-2 Transmittal</MenuItem>
                  <MenuItem value="local_eit">Local EIT Return — Quarterly, per collector</MenuItem>
                  <MenuItem value="local_lst">Local Services Tax — Quarterly, per collector</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={FORM_HELP[genType] || ''}><InfoOutlined fontSize="small" color="action" /></Tooltip>
              </Box>
            </Grid>

            {QUARTERLY_GEN.has(genType) && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Quarter</InputLabel>
                  <Select value={genQuarter} label="Quarter" onChange={e => setGenQuarter(e.target.value)}>
                    <MenuItem value={1}>Q1 (Jan-Mar)</MenuItem>
                    <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                    <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                    <MenuItem value={4}>Q4 (Oct-Dec)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {genType === 'w2' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Employee</InputLabel>
                  <Select value={genEmployeeId} label="Employee" onChange={e => setGenEmployeeId(e.target.value)}>
                    {employees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.last_name}, {emp.first_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert severity="info" variant="outlined">
                Tax Year: <strong>{yearFilter}</strong>. Data is calculated from all processed payroll runs in this year.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating || (genType === 'w2' && !genEmployeeId)}>
            {generating ? <CircularProgress size={20} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
